// A button that stays disabled until the cursor has rested on it continuously
// for N seconds ("hover to arm"). Used for actions that are legitimate but
// easy to fire by accident — e.g. stamping a CFDI to Público en General from
// the invoicing screens, which is irreversible once the SAT has it.
//
// The hover handlers live on a wrapper <div> on purpose: a disabled <button>
// swallows pointer events in Chrome, so mouseenter would never fire on it.
// Leaving the button (or clicking it) disarms, so every use re-arms from zero.

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@shopify/polaris";

function HoverArmButton({
  children,
  onAction,
  holdSeconds = 4,
  loading = false,
  disabled = false,
  tone,
  variant,
}) {
  // Seconds left before the button arms; null once armed.
  const [remaining, setRemaining] = useState(holdSeconds);
  const timerRef = useRef(null);

  const clear = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => clear, []);

  const startHold = () => {
    if (disabled || loading || timerRef.current || remaining === 0) return;
    timerRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clear();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const cancelHold = () => {
    clear();
    setRemaining(holdSeconds);
  };

  const armed = remaining === 0;

  return (
    <div
      onMouseEnter={startHold}
      onMouseLeave={cancelHold}
      style={{ display: "inline-block" }}
      title={
        armed
          ? undefined
          : `Mantén el cursor ${holdSeconds} segundos para habilitar`
      }
    >
      {/* pointer-events:none while disarmed: a disabled <button> eats pointer
          events in Chrome, so without this the wrapper never sees mouseenter
          and the countdown would never start. */}
      <div style={armed ? undefined : { pointerEvents: "none" }}>
        <Button
          variant={variant}
          tone={tone}
          loading={loading}
          disabled={disabled || loading || !armed}
          onClick={() => {
            cancelHold();
            onAction();
          }}
        >
          {armed ? children : `${children} (${remaining})`}
        </Button>
      </div>
    </div>
  );
}

export default HoverArmButton;
